import { useEffect, useState } from 'react'

import { previewCatalogMatch } from '../api/catalogImport'
import type { MatchCandidateDto } from '../api/catalogImportTypes'
import type { ProductTypeDto } from '../api/productTypes'
import { ProductTypeCascadePicker } from './ProductTypeCascadePicker'
import { SuggestTextField } from './SuggestTextField'
import { formatProductTypeLabel } from '../screens/catalogFilters'
import {
  assignCatalogProductType,
  deleteCatalogItem,
  updateCatalogItemFields,
  type CatalogItemFieldsUpdate,
  type SupplierCatalogItemDto,
} from '../api/supplierCatalog'
import {
  assignRestaurantMyPricesProductType,
  deleteRestaurantMyPricesItem,
  updateRestaurantMyPricesItemFields,
} from '../api/restaurantMyPrices'

export type CatalogItemEditMode = 'supplier' | 'restaurant-my-prices'

type CatalogItemEditPanelProps = {
  item: SupplierCatalogItemDto
  organizationId: number
  productTypes: ProductTypeDto[]
  brandOptions?: string[]
  countryOptions?: string[]
  catalogMode?: CatalogItemEditMode
  onSaved: (item: SupplierCatalogItemDto) => void
  onDeleted: () => void
}

export const CatalogItemEditPanel = ({
  item,
  organizationId,
  productTypes,
  brandOptions = [],
  countryOptions = [],
  catalogMode = 'supplier',
  onSaved,
  onDeleted,
}: CatalogItemEditPanelProps) => {
  const [name, setName] = useState(item.supplierName)
  const [brand, setBrand] = useState(item.brand ?? '')
  const [country, setCountry] = useState(item.country ?? '')
  const [price, setPrice] = useState(item.price?.toString() ?? '')
  const [unit, setUnit] = useState(item.unit ?? '')
  const [minShipmentQty, setMinShipmentQty] = useState(item.minShipmentQty?.toString() ?? '')
  const [packageSize, setPackageSize] = useState(item.packageSize ?? '')
  const [comment, setComment] = useState(item.operatorComment ?? '')
  const [productTypeId, setProductTypeId] = useState<number | null>(item.productTypeId)
  const [candidates, setCandidates] = useState<MatchCandidateDto[]>([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateFields = async (
    itemId: number,
    update: CatalogItemFieldsUpdate,
  ): Promise<SupplierCatalogItemDto> =>
    catalogMode === 'restaurant-my-prices'
      ? updateRestaurantMyPricesItemFields(itemId, update)
      : updateCatalogItemFields(itemId, update)

  const assignProductType = async (
    itemId: number,
    productTypeId: number,
  ): Promise<SupplierCatalogItemDto> =>
    catalogMode === 'restaurant-my-prices'
      ? assignRestaurantMyPricesProductType(itemId, productTypeId)
      : assignCatalogProductType(itemId, productTypeId)

  const deleteItem = async (itemId: number): Promise<number> =>
    catalogMode === 'restaurant-my-prices'
      ? deleteRestaurantMyPricesItem(itemId)
      : deleteCatalogItem(itemId)

  useEffect(() => {
    setName(item.supplierName)
    setBrand(item.brand ?? '')
    setCountry(item.country ?? '')
    setPrice(item.price?.toString() ?? '')
    setUnit(item.unit ?? '')
    setMinShipmentQty(item.minShipmentQty?.toString() ?? '')
    setPackageSize(item.packageSize ?? '')
    setComment(item.operatorComment ?? '')
    setProductTypeId(item.productTypeId)
  }, [item])

  useEffect(() => {
    const status = item.matchStatus.toLowerCase()
    if (status !== 'suggested' && status !== 'unmatched') {
      return
    }
    const queryName = name.trim()
    if (!queryName) {
      return
    }
    setLoadingSuggestions(true)
    void previewCatalogMatch(organizationId, queryName, brand.trim() || null)
      .then((preview) => setCandidates(preview.candidates))
      .catch(() => setCandidates([]))
      .finally(() => setLoadingSuggestions(false))
  }, [organizationId, name, brand, item.matchStatus])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      let updated = item
      const priceValue = price.trim() ? Number(price.replace(',', '.')) : null
      const minShipmentValue = minShipmentQty.trim() ? Number(minShipmentQty.replace(',', '.')) : null
      const trimmedName = name.trim()
      const nameChanged = trimmedName !== item.supplierName
      const brandChanged = brand.trim() !== (item.brand ?? '')
      const countryChanged = country.trim() !== (item.country ?? '')
      const priceChanged = priceValue !== item.price
      const unitChanged = unit.trim() !== (item.unit ?? '')
      const minShipmentChanged = minShipmentValue !== item.minShipmentQty
      const packageSizeChanged = packageSize.trim() !== (item.packageSize ?? '')
      const commentChanged = comment.trim() !== (item.operatorComment ?? '')
      const typeChanged = productTypeId !== item.productTypeId

      if (!trimmedName) {
        setError('Укажите наименование позиции.')
        setSaving(false)
        return
      }

      if (
        nameChanged ||
        brandChanged ||
        countryChanged ||
        priceChanged ||
        unitChanged ||
        minShipmentChanged ||
        packageSizeChanged ||
        commentChanged
      ) {
        updated = await updateFields(item.id, {
          ...(nameChanged ? { name: trimmedName } : {}),
          ...(brandChanged ? { brand: brand.trim() || null } : {}),
          ...(countryChanged ? { country: country.trim() || null } : {}),
          ...(priceChanged ? { price: priceValue } : {}),
          ...(unitChanged ? { unit: unit.trim() || null } : {}),
          ...(minShipmentChanged ? { minShipmentQty: minShipmentValue } : {}),
          ...(packageSizeChanged ? { packageSize: packageSize.trim() || null } : {}),
          ...(commentChanged ? { comment: comment.trim() || null } : {}),
        })
      }
      if (typeChanged && productTypeId != null) {
        updated = await assignProductType(item.id, productTypeId)
      }
      onSaved(updated)
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Удалить позицию «${item.supplierName}»?`)) {
      return
    }
    setDeleting(true)
    setError(null)
    try {
      await deleteItem(item.id)
      onDeleted()
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Не удалось удалить')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="market-catalog-edit">
      <div className="market-catalog-edit__meta">
        {item.externalSku ? <span>SKU: {item.externalSku}</span> : null}
        {item.unit ? <span>Ед.: {item.unit}</span> : null}
        {item.minShipmentQty != null ? <span>Мин. отгрузка: {item.minShipmentQty}</span> : null}
        {item.packageSize ? <span>Упаковка: {item.packageSize}</span> : null}
        {item.matchConfidence != null ? (
          <span>Уверенность: {Math.round(item.matchConfidence * 100)}%</span>
        ) : null}
      </div>

      <div className="market-catalog-edit__grid">
        <label className="echat-field market-catalog-edit__field--wide">
          <span className="echat-field__label">Наименование</span>
          <input
            className="echat-field__input"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Цена</span>
          <input
            className="echat-field__input"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Ед. изм.</span>
          <input className="echat-field__input" value={unit} onChange={(e) => setUnit(e.target.value)} />
        </label>
        <SuggestTextField
          label="Бренд"
          value={brand}
          suggestions={brandOptions}
          listId={`catalog-item-${item.id}-brand`}
          onChange={setBrand}
        />
        <SuggestTextField
          label="Страна"
          value={country}
          suggestions={countryOptions}
          listId={`catalog-item-${item.id}-country`}
          onChange={setCountry}
        />
        <label className="echat-field">
          <span className="echat-field__label">Мин. отгрузка</span>
          <input
            className="echat-field__input"
            inputMode="decimal"
            value={minShipmentQty}
            onChange={(e) => setMinShipmentQty(e.target.value)}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Упаковка</span>
          <input
            className="echat-field__input"
            value={packageSize}
            onChange={(e) => setPackageSize(e.target.value)}
          />
        </label>
        {item.productTypeId != null && item.productTypeName ? (
          <p className="echat-muted">
            Сохранённая категория:{' '}
            {formatProductTypeLabel(
              item.productTypeId,
              item.productTypeName,
              item.productTypeActive,
              new Map(),
            )}
          </p>
        ) : null}
        {catalogMode !== 'restaurant-my-prices' ? (
          <ProductTypeCascadePicker
            productTypes={productTypes}
            value={productTypeId}
            onChange={setProductTypeId}
            className="market-product-type-cascade market-product-type-cascade--form"
            idPrefix={`catalog-item-${item.id}`}
          />
        ) : null}
      </div>

      {catalogMode === 'restaurant-my-prices' ? (
        <div className="market-catalog-edit__types market-catalog-edit__types--split">
          <ProductTypeCascadePicker
            productTypes={productTypes}
            value={productTypeId}
            onChange={setProductTypeId}
            showCascadeLevels={false}
            quickSearchClassName="market-catalog-edit__field--wide"
            idPrefix={`catalog-item-${item.id}`}
          />
          <ProductTypeCascadePicker
            productTypes={productTypes}
            value={productTypeId}
            onChange={setProductTypeId}
            showQuickSearch={false}
            className="market-product-type-cascade market-product-type-cascade--levels-only"
            idPrefix={`catalog-item-${item.id}-levels`}
          />
        </div>
      ) : null}

      {loadingSuggestions ? <p className="echat-muted">Загрузка подсказок…</p> : null}
      {candidates.length > 0 ? (
        <div className="market-catalog-edit__suggestions">
          <p className="echat-field__label">Подсказки матчера</p>
          <div className="market-inline-actions">
            {candidates.slice(0, 5).map((candidate) => (
              <button
                key={candidate.productTypeId}
                type="button"
                className="echat-btn echat-btn--ghost echat-btn--sm"
                onClick={() => setProductTypeId(candidate.productTypeId)}
              >
                {candidate.name} ({Math.round(candidate.score * 100)}%)
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <label className="echat-field">
        <span className="echat-field__label">Комментарий</span>
        <textarea
          className="echat-field__input"
          rows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </label>

      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="market-inline-actions">
        <button
          type="button"
          className="echat-btn echat-btn--primary"
          disabled={saving || deleting}
          onClick={() => void handleSave()}
        >
          {saving ? 'Сохранение…' : 'Сохранить'}
        </button>
        <button
          type="button"
          className="echat-btn echat-btn--danger"
          disabled={saving || deleting}
          onClick={() => void handleDelete()}
        >
          {deleting ? 'Удаление…' : 'Удалить'}
        </button>
      </div>
    </div>
  )
}
